import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Info, Pencil, X } from 'lucide-react';

/**
 * Botón ℹ️ clickeable para los widgets del dashboard. Al hacer clic abre un
 * popover chico (mini-modal) con título + descripción de qué representa el
 * widget. Se cierra con clic afuera, la X o Escape.
 *
 * Reemplaza al tooltip nativo (`title=`) por hover: ahora es un popover propio,
 * legible y con estilo de marca.
 *
 * Se renderiza en un PORTAL con position:fixed (no `absolute`) para que NO lo
 * recorte ningún contenedor con `overflow-hidden` — las KPI cards de gradiente
 * lo tienen por la sparkline decorativa.
 *
 * `tone='light'` pinta el ícono en blanco translúcido para que contraste sobre
 * las cards de gradiente de color (Total Ventas, Productos, etc.).
 */
export function InfoPopover({
  title,
  body,
  hint,
  align = 'left',
  tone = 'default',
  editValue,
  onSaveHelp,
  editFirst = false,
}: {
  title: string;
  /** Texto descriptivo. Los párrafos se separan con doble salto de línea. */
  body: string;
  /** Línea al pie, en gris (ej. "Clic en la card para ver el detalle."). */
  hint?: string;
  align?: 'left' | 'right';
  tone?: 'default' | 'light';
  /** Texto editable (solo la ayuda, sin la regla maestra). Requiere `onSaveHelp` para editar. */
  editValue?: string;
  /** Si se pasa (admin), el popover muestra un lápiz para editar la ayuda. */
  onSaveHelp?: (text: string) => void;
  /** Al abrir, ir DIRECTO al editor (modo edición del tablero) en vez del texto de solo lectura. */
  editFirst?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  const POP_WIDTH = 320; // w-80

  useLayoutEffect(() => {
    if (!open) return;
    const compute = () => {
      const el = btnRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      let left = align === 'right' ? r.right - POP_WIDTH : r.left;
      left = Math.max(8, Math.min(left, window.innerWidth - POP_WIDTH - 8));
      setCoords({ top: r.bottom + 8, left });
    };
    compute();
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setEditing(false);
  }, [open]);

  const triggerCls = tone === 'light'
    ? (open ? 'text-white bg-white/25' : 'text-white/60 hover:text-white hover:bg-white/15')
    : (open ? 'text-red-600 bg-red-600/10' : 'text-slate-300 hover:text-red-600 hover:bg-red-600/5');

  const paragraphs = body.split('\n\n').filter(Boolean);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        // stopPropagation: el ⓘ vive dentro de la card clickeable (drilldown).
        // Sin esto, abrir info dispararía también el drill-down de la card.
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => {
            const next = !o;
            // editFirst (modo edición): al abrir, entrar directo al editor de la ayuda.
            if (next && editFirst && onSaveHelp) { setDraft(editValue ?? ''); setEditing(true); }
            return next;
          });
        }}
        onKeyDown={(e) => { e.stopPropagation(); }}
        title="¿Qué es esto?"
        aria-label={`Información: ${title}`}
        className={`no-drag grid place-items-center rounded-full p-1 transition-colors shrink-0 ${triggerCls}`}
      >
        <Info className="h-3.5 w-3.5" />
      </button>
      {open && coords && typeof document !== 'undefined' && createPortal(
        <div
          ref={popRef}
          onClick={(e) => e.stopPropagation()}
          className="fixed z-[200] w-72 cursor-default rounded-2xl bg-white p-3.5 text-left shadow-[0_16px_44px_rgba(220,38,38,0.16),0_4px_12px_rgba(0,0,0,0.1)] ring-1 ring-slate-200/70 sm:w-80"
          style={{ top: coords.top, left: coords.left }}
        >
          <div className="mb-1.5 flex items-start justify-between gap-2">
            <p className="flex items-center gap-1.5 text-[13px] font-bold leading-snug text-slate-800">
              <Info className="h-3.5 w-3.5 shrink-0 text-red-600" />
              {title}
            </p>
            <div className="-mr-1 -mt-0.5 flex shrink-0 items-center gap-1">
              {onSaveHelp && !editing && (
                <button
                  type="button"
                  onClick={() => { setDraft(editValue ?? ''); setEditing(true); }}
                  className="text-slate-300 hover:text-red-600"
                  title="Editar ayuda"
                  aria-label="Editar ayuda"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-slate-300 hover:text-slate-600"
                aria-label="Cerrar"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          {editing ? (
            <div>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={4}
                autoFocus
                className="w-full resize-none rounded-lg border border-slate-300 p-2 text-[11.5px] leading-relaxed text-slate-700 outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
              />
              <div className="mt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="rounded-md px-2.5 py-1 text-[11px] font-medium text-slate-500 hover:bg-slate-100"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => { onSaveHelp?.(draft.trim()); setEditing(false); setOpen(false); }}
                  className="rounded-md bg-red-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-red-700"
                >
                  Guardar
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-1.5 text-[11.5px] leading-relaxed text-slate-600">
              {paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
              {hint && <p className="text-slate-400">{hint}</p>}
            </div>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}
